import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, MapPin, Heart, User, LogOut, RotateCcw, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import api, { eur, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { STATUS_LABELS, StatusBadge } from "@/pages/OrderTracking";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [newAddr, setNewAddr] = useState({ label: "", address: "", city: "", postal_code: "", country: "Ελλάδα" });

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [user, loading, navigate]);
  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || "", phone: user.phone || "" });
      api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
      api.get("/addresses").then(({ data }) => setAddresses(data)).catch(() => {});
    }
  }, [user]);

  if (!user) return <div className="min-h-[60vh]" />;

  const saveProfile = async () => { try { await api.put("/profile", profile); toast.success("Το προφίλ ενημερώθηκε"); } catch (e) { toast.error(apiErr(e)); } };
  const addAddress = async () => {
    if (!newAddr.address || !newAddr.city) { toast.error("Συμπλήρωσε τη διεύθυνση"); return; }
    const { data } = await api.post("/addresses", newAddr);
    setAddresses((a) => [...a, data]); setNewAddr({ label: "", address: "", city: "", postal_code: "", country: "Ελλάδα" }); toast.success("Η διεύθυνση προστέθηκε");
  };
  const delAddress = async (id) => { await api.delete(`/addresses/${id}`); setAddresses((a) => a.filter((x) => x.id !== id)); };
  const reorder = (order) => { order.items.forEach((it) => addItem({ product_id: it.product_id, slug: it.slug, name: it.name, color: it.color, size: it.size, price: it.price, quantity: it.quantity, image: it.image, max_stock: 99 }, true)); toast.success("Τα προϊόντα προστέθηκαν στο καλάθι"); navigate("/cart"); };

  const TABS = [["orders", "Παραγγελίες", Package], ["addresses", "Διευθύνσεις", MapPin], ["profile", "Προφίλ", User]];
  const inp = "w-full bg-[#0A0A0B] border border-zinc-700 rounded px-3 py-2.5 text-sm outline-none focus:border-[#FF2E4C]";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#FF2E4C] mb-1">ΛΟΓΑΡΙΑΣΜΟΣ</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter">Γεια σου, {user.name?.split(" ")[0] || "SKGWEAR"}</h1>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-[#FF2E4C]" data-testid="logout-button"><LogOut className="w-4 h-4" /> Αποσύνδεση</button>
      </div>

      <div className="flex gap-2 border-b border-zinc-800 mb-8 overflow-x-auto no-scrollbar">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-display font-bold uppercase tracking-tight border-b-2 whitespace-nowrap ${tab === id ? "border-[#FF2E4C] text-white" : "border-transparent text-zinc-500 hover:text-white"}`} data-testid={`account-tab-${id}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
        <Link to="/wishlist" className="flex items-center gap-2 px-4 py-3 text-sm font-display font-bold uppercase tracking-tight text-zinc-500 hover:text-white"><Heart className="w-4 h-4" /> Αγαπημένα</Link>
      </div>

      {tab === "orders" && (
        <div className="space-y-4">
          {orders.length === 0 ? <p className="text-zinc-500">Δεν έχεις παραγγελίες ακόμα. <Link to="/shop" className="text-[#FF2E4C]">Ξεκίνα τα ψώνια</Link></p> :
            orders.map((o) => (
              <div key={o.id} className="card-surface rounded-lg p-5" data-testid="account-order-tracking-card">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <p className="font-display font-bold uppercase">#{o.order_number}</p>
                    <p className="text-xs text-zinc-500 font-mono">{new Date(o.created_at).toLocaleDateString("el-GR")} · {o.items.length} προϊόντα</p>
                  </div>
                  <StatusBadge status={o.status} />
                </div>
                <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
                  {o.items.map((it, i) => <img key={i} src={it.image} alt="" className="w-14 h-16 object-cover rounded shrink-0" />)}
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{eur(o.total)}</span>
                  <div className="flex gap-2">
                    <button onClick={() => reorder(o)} className="flex items-center gap-1.5 text-xs font-bold uppercase border border-zinc-700 rounded px-3 py-2 hover:border-[#FF2E4C]" data-testid="reorder-button"><RotateCcw className="w-3.5 h-3.5" /> Reorder</button>
                    <Link to={`/tracking/${o.order_number}`} className="text-xs font-bold uppercase bg-white text-black rounded px-3 py-2 hover:bg-zinc-200">Λεπτομέρειες</Link>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {tab === "addresses" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            {addresses.map((a) => (
              <div key={a.id} className="card-surface rounded-lg p-4 flex justify-between items-start">
                <div><p className="font-semibold text-sm">{a.label || "Διεύθυνση"}</p><p className="text-sm text-zinc-400">{a.address}, {a.city} {a.postal_code}</p><p className="text-xs text-zinc-500">{a.country}</p></div>
                <button onClick={() => delAddress(a.id)} className="text-zinc-600 hover:text-[#FF2E4C]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {addresses.length === 0 && <p className="text-zinc-500 text-sm">Δεν έχεις αποθηκευμένες διευθύνσεις.</p>}
          </div>
          <div className="card-surface rounded-lg p-5">
            <h3 className="font-display font-bold uppercase text-sm mb-3">Νέα Διεύθυνση</h3>
            <div className="space-y-2">
              <input placeholder="Ετικέτα (π.χ. Σπίτι)" value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} className={inp} />
              <input placeholder="Διεύθυνση" value={newAddr.address} onChange={(e) => setNewAddr({ ...newAddr, address: e.target.value })} className={inp} />
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Πόλη" value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} className={inp} />
                <input placeholder="Τ.Κ." value={newAddr.postal_code} onChange={(e) => setNewAddr({ ...newAddr, postal_code: e.target.value })} className={inp} />
              </div>
              <button onClick={addAddress} className="btn-red w-full py-2.5 text-xs font-bold uppercase rounded flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Προσθήκη</button>
            </div>
          </div>
        </div>
      )}

      {tab === "profile" && (
        <div className="max-w-md card-surface rounded-lg p-6">
          <div className="space-y-3">
            <div><label className="text-xs font-mono uppercase text-zinc-500">Ονοματεπώνυμο</label><input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={inp} /></div>
            <div><label className="text-xs font-mono uppercase text-zinc-500">Τηλέφωνο</label><input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={inp} /></div>
            <div><label className="text-xs font-mono uppercase text-zinc-500">Email</label><input value={user.email} disabled className={`${inp} opacity-60`} /></div>
            <button onClick={saveProfile} className="btn-red w-full py-3 text-xs font-bold uppercase rounded" data-testid="save-profile">Αποθήκευση</button>
          </div>
        </div>
      )}
    </div>
  );
}
